import { MeasureKind } from '../services/measure-detail.service';
import { boldName } from './delete-confirmation';

/**
 * Pourquoi la suppression d'une mesure / d'un poste d'analyse est bloquée (CES-605).
 * Priorité au blocage « référencé » : l'utilisateur peut agir dessus (re-cibler
 * les postes), contrairement aux valeurs qui exigent un archivage. Les valeurs
 * ne bloquent que les mesures : celles d'un poste d'analyse sont des données
 * dérivées de sa formule, purgées avec lui par le backend.
 */
export type MeasureDeletionBlock = { reason: 'referenced'; names: string } | { reason: 'used' } | null;

interface NamedReference {
  name?: string | null;
}

export function resolveMeasureDeletionBlock(
  kind: MeasureKind,
  hasValues: boolean,
  referencing: readonly NamedReference[],
): MeasureDeletionBlock {
  if (referencing.length > 0) {
    return { reason: 'referenced', names: referencing.map(r => r.name ?? '?').join(', ') };
  }
  return kind !== 'analysis-post' && hasValues ? { reason: 'used' } : null;
}

interface MeasureDeletionKeys {
  confirm: string;
  used: string;
  referenced: string;
}

const MEASURE_KEYS: MeasureDeletionKeys = {
  confirm: 'entities.measure.messages.deleteConfirm',
  used: 'entities.measure.messages.cannotDeleteUsed',
  referenced: 'entities.measure.messages.referencedByAnalysisPost',
};

const ANALYSIS_POST_KEYS: MeasureDeletionKeys = {
  confirm: 'entities.measure.messages.deleteConfirmAnalysisPost',
  used: 'entities.measure.messages.cannotDeleteUsedAnalysisPost',
  referenced: 'entities.measure.messages.referencedByAnalysisPostForPost',
};

export function measureDeletionKeys(kind: MeasureKind): MeasureDeletionKeys {
  return kind === 'analysis-post' ? ANALYSIS_POST_KEYS : MEASURE_KEYS;
}

export interface AnalysisPostDeletionImpactSummary {
  computedValues: number;
  tables: readonly NamedReference[];
  graphics: readonly NamedReference[];
}

export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * Phrases ajoutées à la confirmation de suppression d'un poste d'analyse pour que
 * l'utilisateur supprime en connaissance de cause : valeurs calculées purgées,
 * retrait des tableaux et graphiques. Prose naturelle plutôt que liste à puces :
 * les retraits sont assemblés en UNE phrase à partir de fragments ICU par locale
 * (« du tableau X », « des graphiques Y, Z ») reliés par la conjonction traduite,
 * ce qui garde une grammaire correcte dans les quatre langues. `count` n'est
 * jamais affiché, il accorde le singulier/pluriel. Les noms sont mis en gras et
 * échappés (le dialog rend via innerHTML).
 */
export function analysisPostDeletionDetailSentences(impact: AnalysisPostDeletionImpactSummary, translate: TranslateFn): string[] {
  const joinNames = (refs: readonly NamedReference[]): string => boldName(refs.map(ref => ref.name ?? '?').join(', '));
  const sentences: string[] = [];
  if (impact.computedValues > 0) {
    sentences.push(translate('entities.measure.messages.deleteImpactValues', { count: impact.computedValues }));
  }
  const membershipFragments: string[] = [];
  if (impact.tables.length > 0) {
    membershipFragments.push(
      translate('entities.measure.messages.deleteImpactTablesFragment', {
        count: impact.tables.length,
        names: joinNames(impact.tables),
      }),
    );
  }
  if (impact.graphics.length > 0) {
    membershipFragments.push(
      translate('entities.measure.messages.deleteImpactGraphicsFragment', {
        count: impact.graphics.length,
        names: joinNames(impact.graphics),
      }),
    );
  }
  if (membershipFragments.length > 0) {
    sentences.push(
      translate('entities.measure.messages.deleteImpactRemovedFrom', {
        fragments: membershipFragments.join(translate('entities.measure.messages.deleteImpactJoin')),
      }),
    );
  }
  return sentences;
}
